(ns ui.editor2.common
  (:require [clojure.set :as set]
            [datascript.core :as ds]
            [storrito.editor.music-sticker :as music-sticker]
            ["jszip" :as JSZip]
            ["gifuct-js" :as gifuct-js]))

(defn constrain-hiccup-width
  "Adds a fixed width with border-box sizing to the hiccup's outer wrapper div,
   so that text wraps at this width during measurement and rendering."
  [hiccup width]
  (update-in hiccup [1 :style] assoc
             :width (str width "px")
             :box-sizing "border-box"))

(def media-upload-input-id "editor-media-upload")

(def tooltip-position
  {:top ["bottom-full" "mb-2"
         "left-1/2"
         "-translate-x-1/2"]
   :left ["top-1"
          "-translate-y-1/2"]
   :right ["mr-2"
           "top-1/2"
           "-translate-y-1/2"]
   :bottom ["top-full" "mt-2"
            "left-1/2"
            "-translate-x-1/2"]
   })

(defn tool-tip
  [{:keys [element text position]}]
  [:div.relative.group.inline-block.hover:z-50
   element
   [:div.absolute.w-max.px-2.py-1.bg-gray-800.border.border-gray-500.text-white.text-sm.rounded.opacity-0.group-hover:opacity-100.transition-opacity.duration-300.pointer-events-none
    {:class (get tooltip-position position)
     :style {:z-index 99999}}
      text]])

(defn s->ms
  [s]
  (long (* s
           1000)))

(defn q-timeline-uuid
  [db]
  (ds/q '[:find ?timeline-uuid .
          :where
          [?timeline :timeline/uuid ?timeline-uuid]]
        db))

(defn q-visible
  [state]
  (let [editor2 (:editor2 state)]
    (ds/q
      '[:find
        [?clip-uuid ...]
        :in $ ?playhead-position [?asset-type ...]
        :where
        [?clip :clip/uuid ?clip-uuid]
        [?clip :clip/start ?start]
        [?clip :clip/end ?end]
        [(<= ?start ?playhead-position)]
        [(<= ?playhead-position ?end)]
        [?clip :clip/asset ?asset]
        ]
      (:db editor2)
      (:playhead-position editor2)
      [:video
       :gif
       :image])))

(defn q-track-order
  [db]
  (ds/q
    '[:find
      ?tracks .
      :where
      [?e :timeline/tracks ?tracks]]
    db))

(defn get-visible-clips
  "The order of the tracks in the timeline should define the order in which the
   contents (like image or video) is painted onto the design. Consequently, the
   contents of the first track will overlay every content of lower tracks."
  [state]
  (let [db (get-in state
                   [:editor2
                    :db])
        clip-uuids (q-visible state)
        track-order-index (into {}
                                (map-indexed
                                  (fn [idx track-uuid]
                                    [track-uuid idx])
                                  (q-track-order db)))
        clips (map (fn [clip-uuid]
                     (ds/entity db
                                [:clip/uuid clip-uuid]))
                   clip-uuids)]
    (sort-by
      (fn [clip]
        [
         ;; Track order needs to be reversed so that tracks that show up
         ;; first in timeline are rendered on top in the editor
         (- (get track-order-index
                 (:track/uuid (:clip/track clip))))
         ;; Sort clips that are in the same track by :clip/start and then by :clip/uuid.
         ;; By :clip/start ensures slideshows with overlapping clips in one track render correctly.
         ;; By :clip/uuid ensures that the server-side render order is deterministic and the same as in the editor.
         (:clip/start clip)
         (:clip/uuid clip)])
      clips)))

(defn q-all-clips
  [db]
  (ds/q '[:find
          [?clip-uuid ...]
          :where
          [?clip :clip/uuid ?clip-uuid]]
        db))

(defn q-all-audio-clips
  [db]
  (ds/q '[:find
          [?clip-uuid ...]
          :where
          [?asset :asset/type :audio]
          [?clip :clip/asset ?asset]
          [?clip :clip/uuid ?clip-uuid]
          ]
        db))

(defn get-all-audio-clips
  [state]
  (let [db (get-in state
                   [:editor2
                    :db])
        clip-uuids (q-all-audio-clips db)]
    (map (fn [clip-uuid]
           (ds/entity db
                      [:clip/uuid clip-uuid]))
         clip-uuids)))

(defn get-all-clips
  [state]
  (let [db (get-in state
                   [:editor2
                    :db])
        clip-uuids (q-all-clips db)
        track-order-index (into {}
                                (map-indexed
                                  (fn [idx track-uuid]
                                    [track-uuid idx])
                                  (q-track-order db)))
        clips (map (fn [clip-uuid]
                     (ds/entity db
                                [:clip/uuid clip-uuid]))
                   clip-uuids)]
    (sort-by
      (fn [clip]
        [
         ;; Track order needs to be reversed so that tracks that show up
         ;; first in timeline are rendered on top in the editor
         (- (get track-order-index
                 (:track/uuid (:clip/track clip))))
         ;; Sort clips that are in the same track by :clip/start and then by :clip/uuid.
         ;; By :clip/start ensures slideshows with overlapping clips in one track render correctly.
         ;; By :clip/uuid ensures that the server-side render order is deterministic and the same as in the editor.
         (:clip/start clip)
         (:clip/uuid clip)])
      clips)))

(defn get-visible-video-clips
  [state]
  (filter
    (fn [clip]
      (= (get-in clip
                 [:clip/asset
                  :asset/type])
         :video))
    (get-visible-clips state)))

(def zoom-in-icon
  [:svg
   {:xmlns "http://www.w3.org/2000/svg",
    :fill "none",
    :viewBox "0 0 24 24",
    :stroke-width "1.5",
    :stroke "currentColor",
    :class ["size-5" "text-slate-400"]}
   [:path
    {:stroke-linecap "round",
     :stroke-linejoin "round",
     :d
     "m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607ZM10.5 7.5v6m3-3h-6"}]])

(def zoom-out-icon
  [:svg
   {:xmlns "http://www.w3.org/2000/svg",
    :fill "none",
    :viewBox "0 0 24 24",
    :stroke-width "1.5",
    :stroke "currentColor",
    :class ["size-5" "text-slate-400"]}
   [:path
    {:stroke-linecap "round",
     :stroke-linejoin "round",
     :d
     "m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607ZM13.5 10.5h-6"}]])

(defn transact-db
  [db tx]
  (:db-after
   (ds/with db
            tx)))

(defn transact
  "Transacts `tx` into the editor2 db of `state`. Without a db (the
   design is not loaded yet or the editor was left, e.g. a debounced
   or upload callback that arrives afterwards) the state stays as it
   is: DataScript's `-attrs-by` on nil threw instead (STORRITO-V2-KM)."
  [state tx]
  (if (get-in state [:editor2 :db])
    (update-in state
               [:editor2 :db]
               (fn [db]
                 (transact-db db
                              tx)))
    (do (js/console.warn "editor2 transact without a db, dropped:" (pr-str tx))
        state)))

(defn download!
  [{:keys [content file-name]}]
  (let [link (.createElement js/document "a")]
    (set! (.-href link) (js/URL.createObjectURL content))
    (set! (.-download link) file-name)
    (.appendChild js/document.body link)
    (.click link)
    (.removeChild js/document.body link)))

(defn create-zip
  [contents]
  (let [zip (JSZip.)]
    (doseq [[path content] contents]
      (.file zip
             path
             content))
    (.generateAsync zip (clj->js {:type "blob"}))))

(def uuid-regex #"[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}")

(defn uuid-str? [str]
  (boolean (and (string? str) (re-matches uuid-regex str))))

(defn get-gif-length [{:keys [url]}]
  (js/Promise.
    (fn [resolve reject]
      (-> (js/fetch url)
          (.then (fn [response]
                   (.arrayBuffer response)))
          (.then (fn [buffer]
                   (let [gif (.parseGIF gifuct-js buffer)
                         frames (.decompressFrames gifuct-js
                                                   gif
                                                   true)
                         total-delay (reduce (fn [acc frame]
                                               (+ acc (.-delay frame)))
                                             0
                                             frames)]
                     (resolve {:ms total-delay}))))
          (.catch reject)
          ))))

(comment
  (.then (get-gif-length {:url "https://media4.giphy.com/media/v1.Y2lkPTc5MGI3NjExdzhqcTVhMnh6Nmlzbm1zcGprcThuYmtkMmJyY21mdGllaThrNzZwNiZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9cw/iEbPnIPyh9Exq/giphy.gif"})
         prn)

  )

(defn achain
  "Chains a sequence of asynchronous functions, handling both promise-returning and plain value functions.

   Each function in the sequence is applied to the resolved value of the previous
   function. If a function returns a non-promise value, it is automatically
   wrapped using `js/Promise.resolve` so that the chaining remains consistent.

   Parameters:
   - initial-value: The initial value to be passed into the chain.
   - async-fns: A sequence of functions that take a single argument (the resolved value of the previous computation) and return either a plain value or a JS Promise.

   Returns: A JS Promise that resolves to the final value after all functions in
   the chain have been applied."
  [initial-value async-fns]
  (reduce (fn [p f]
            (.then (js/Promise.resolve p)
                   (fn [result]
                     (f result))))
          (js/Promise.resolve initial-value)
          async-fns))

(defn get-video-length [{:keys [url]}]
  (js/Promise.
    (fn [resolve reject]
      (let [video (.createElement js/document "video")]
        ;; Only metadata is read here. Do not set `crossOrigin`: Editor1
        ;; design generation loads `/blob/...` URLs that redirect to GCS, and
        ;; legacy GCS objects are not guaranteed to send CORS headers.
        (set! (.-preload video) "auto")
        (set! (.-src video) url)
        (.addEventListener video "loadedmetadata"
                           (fn [_e]
                             (resolve {:ms (s->ms (.-duration video))})))
        (.addEventListener video
                           "error"
                           (fn [_e]
                             (reject (js/Error.
                                       (str "Failed to load video metadata: " url)))))))))

(comment
  (.then
    (get-video-length
      {:url "https://media1.giphy.com/media/lJNoBCvQYp7nq/giphy.mp4?cid=b100373enztydndbd4mxvmm0a8vzkl2pef2ra6y54n2f9i67&ep=v1_gifs_search&rid=giphy.mp4&ct=g"})
    prn)
  )

(defn q-all-asset-uuids
  [db]
  (ds/q '[:find
          [?asset-uuid ...]
          :where
          [?asset :asset/uuid ?asset-uuid]]
        db))

(defn get-all-assets
  [db]
  (map (fn [asset-uuid]
         (ds/entity db
                    [:asset/uuid asset-uuid]))
       (q-all-asset-uuids db)))

(defn q-tracks
  [db]
  (ds/q '[:find
          [?uuid ...]
          :where
          [?track :track/uuid ?uuid]]
        db))

(defn get-tracks
  [db]
  (map (fn [uuid]
         (ds/entity db
                    [:track/uuid uuid]))
       (q-tracks db)))

(defn slide-clips
  [db]
  (let [timeline (ds/entity db
                            [:timeline/uuid
                             (q-timeline-uuid db)])
        slide-uuids (:timeline/slides timeline)
        slides (map
                 (fn [slide-uuid]
                   (ds/entity db
                              [:slide/uuid slide-uuid]))
                 slide-uuids)]
    (second
      (reduce
        (fn [[total slide-clips] slide]
          (let [duration (:slide/duration slide)]
            [(+ total duration)
             (conj slide-clips
                   {:slide/uuid (:slide/uuid slide)
                    :slide/duration duration
                    :slide/start total
                    :slide/end (+ total duration)})]))
        [0 []]
        slides))))

(defn current-slide-uuid
  [{:keys [editor2]}]
  (let [{:keys [db playhead-position]} editor2]
    (some
      (fn [slide]
        (when (and (<= (:slide/start slide)
                       playhead-position)
                   (< playhead-position
                      (:slide/end slide)))
          (:slide/uuid slide)))
      (slide-clips db))))

(defn get-current-slide
  [state]
  (let [db (get-in state
                   [:editor2
                    :db])]
    (when db
      (ds/entity db
                 [:slide/uuid (current-slide-uuid state)]))))

(defn get-selected-clip
  [state]
  (let [{:keys [db selected-clip]} (:editor2 state)]
    (when db
      (ds/entity db
                 [:clip/uuid selected-clip]))))

(defn selected-clip-uuids
  "The full set of currently selected clip UUIDs.

  `:selected-clip` (the UUID of a single clip) is kept as the active anchor used
  by the sidebar and other features that care about a primary selection.
  `:selected-clips` is a (possibly empty) set that is the source of truth for
  the canvas multi-selection. The two are always kept in sync: when a single
  clip is selected the set contains exactly that UUID, when nothing is selected
  the set is empty, and on multi-select the set contains every selected UUID
  including the anchor."
  [state]
  (let [{:keys [selected-clip selected-clips]} (:editor2 state)]
    (cond
      ;; Trust the set as source of truth — including an empty `#{}`, which
      ;; means "selection cleared" and must not silently fall back to a
      ;; stale `:selected-clip` left behind by an older code path.
      (set? selected-clips) selected-clips
      selected-clip #{selected-clip}
      :else #{})))

(defn multi-select?
  "True when more than one clip is currently selected on the canvas."
  [state]
  (>= (count (selected-clip-uuids state)) 2))

(defn get-dragged-clip
  [state]
  (let [{:keys [db drag]} (:editor2 state)
        clip-uuid (:clip/uuid drag)]
    (when db
      (ds/entity db
                 [:clip/uuid clip-uuid]))))

(defn invisible-design-section
  [{:keys [design text]}]
  [:div
   {:class ["flex" "flex-col" "gap-2"]}
   [:div
    {:class ["flex" "flex-col" "w-full"]}
    [:span
     {:class ["text-xs" "font-medium"]}
     "Invisible"]
    [:span
     {:class ["text-xs" "text-gray-400"]}
     "(invisible in story, but functional on Instagram)"]]
   [:ul.grid.grid-cols-1.gap-6.lg:grid-cols-1
    [:li.col-span-1.rounded-lg.shadow.divide-y.divide-gray-200
     [:button.w-full.flex.items-center.justify-center.rounded-md.p-2.outline-1.outline-dashed
      {:type "button"
       :on {:click [[:editor2/set-asset-invisible-design design]
                    [:editor2/prepare-assets!]]}}
      [:span
       {:style {:fontSize "30px"
                :fontFamily "Instagram Sans Condensed"
                :color "#aaa"}}
       text]]]]])

(def interactive-stickers
  #{:poll-sticker :question-sticker :countdown-sticker :quiz-sticker :emoji-slider-sticker
    :music-sticker})

(def static-stickers
  #{:hashtag-sticker :mention-sticker :link-sticker :location-sticker})

(defn is-sticker?
  [asset]
  (boolean (get (set/union
                  interactive-stickers
                  static-stickers)
                (:asset/type asset))))

(defn track-contains-stickers?
  "Check if a track contains any clips with sticker assets."
  [db track-uuid]
  (let [track (ds/entity db [:track/uuid track-uuid])
        clips (:clip/_track track)]
    (boolean
      (some (fn [clip]
              (let [asset (:clip/asset clip)]
                (is-sticker? asset)))
            clips))))

(defn find-overlapping-clips-at-position
  "Find all clips in a track that overlap at a given time position (in ms).
   Returns clips sorted by their position in the track order."
  [db track-uuid position-ms]
  (let [track (ds/entity db [:track/uuid track-uuid])
        clips (:clip/_track track)]
    (->> clips
         (filter (fn [clip]
                   (and (<= (:clip/start clip) position-ms)
                        (>= (:clip/end clip) position-ms))))
         (sort-by :clip/start)
         vec)))

(defn needs-blurred-images
  "Checks if any image assets have legacy filters but no uploaded-uri."
  [state]
  (seq (ds/q '[:find [?asset ...]
               :where
               [?asset :asset/type :image]
               [?asset :asset/data ?data]
               [(get ?data :legacy-filters) ?legacy-filters]]
             (:db (:editor2 state)))))

(def defaults
  (let [timeline-min-zoom 30
        timeline-max-zoom 200
        composition-min-zoom 0.5
        composition-max-zoom 5
        window-width js/window.innerWidth
        window-height js/window.innerHeight
        slide-duration 15.0
        design-width 1080
        design-height 1920
        ]
    {:design/width design-width
     :design/height design-height
     :timeline/min-zoom timeline-min-zoom
     :timeline/max-zoom timeline-max-zoom
     :timeline/snapping-px 10
     :pixel-per-second (min (max (/ window-width
                                    (+ slide-duration 1.0))
                                 timeline-min-zoom)
                            timeline-max-zoom)
     :playhead-position 0 ;; milliseconds
     :status :paused
     :composition-zoom (/ 1.0
                          (/ window-height 1.8 design-height))
     :composition/min-zoom composition-min-zoom
     :composition/max-zoom composition-max-zoom
     :composition/zoom-step 0.2
     :slide/min-duration 1000
     :slide/max-duration (* 60 1000)
     :clip/default-duration 5000
     :sidebar-panel :sticker}))

;; No clip may be scaled to zero or mirrored into negative scales by a
;; scale gesture:
(def absolute-min-clip-scale 0.01)

(defn min-clip-scale
  "The minimal allowed scale for the clip. The Instagram app does not
   allow shrinking the music stickers below a minimal size (measured);
   mirror those limits in the editor. The simple card's width varies
   with the text, so its minimum is on the fixed card height. All other
   clips get `absolute-min-clip-scale`."
  [clip]
  (let [asset (:clip/asset clip)
        sticker-min
        (when (= (:asset/type asset)
                 :music-sticker)
          (case (get-in asset [:asset/data :display-type])
            "MUSIC_VINYL"
            (when-let [w (:asset/width asset)]
              (/ (* music-sticker/vinyl-min-width-ratio
                    (:design/width defaults))
                 w))

            "MUSIC_OVERLAY_ALBUM_ART"
            (when-let [w (:asset/width asset)]
              (/ (* music-sticker/album-art-min-width-ratio
                    (:design/width defaults))
                 w))

            "MUSIC_OVERLAY_SIMPLE"
            (when-let [h (:asset/height asset)]
              (/ (* music-sticker/simple-min-height-ratio
                    (:design/height defaults))
                 h))

            nil))]
    (max (or sticker-min 0)
         absolute-min-clip-scale)))
